import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'wouter';
import { Sparkles, CheckCircle2, AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsEntitledUser } from '../../hooks/useWarehouseEntitlement';

interface DashboardSummary {
    autoApprovedToday: number;
    needsReview: number;
    waitingOnCarrier: number;
    lastAction: {
        summary: string;
        timestamp: string;
        decision: string;
    } | null;
}

export default function AgentSummaryCard() {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsEntitledUser(user);
    const [, setLocation] = useLocation();
    const [data, setData] = useState<DashboardSummary | null>(null);

    useEffect(() => {
        if (!token || !isOperationsEntitled) {
            setData(null);
            return;
        }
        fetch('/api/agent/dashboard-summary', {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.ok ? res.json() : null)
            .then(setData)
            .catch(() => {});
    }, [token, isOperationsEntitled]);

    if (!isOperationsEntitled || !data) return null;

    const totalActionable = data.needsReview + data.waitingOnCarrier;
    const hasActivity = data.autoApprovedToday > 0 || totalActionable > 0 || data.lastAction;

    if (!hasActivity) return null;

    // Relative time for last action
    const lastActionTime = data.lastAction?.timestamp
        ? (() => {
            const diff = Date.now() - new Date(data.lastAction!.timestamp).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.floor(hrs / 24)}d ago`;
        })()
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary/30 transition-all group"
            onClick={() => setLocation('/agent/inbox')}
        >
            {/* Header */}
            <div className="px-5 py-3 flex items-center gap-2 border-b border-border bg-primary/5">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground font-outfit">AI Dock Coordinator</span>
                <div className="flex-1" />
                <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1">
                    Open Inbox <ArrowRight className="w-3 h-3" />
                </span>
            </div>

            {/* Stats Grid */}
            <div className="px-5 py-4 grid grid-cols-3 gap-4">
                {/* Auto-booked today */}
                <div className="text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-xl font-bold text-foreground font-mono">{data.autoApprovedToday}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Auto-Booked Today</div>
                </div>

                {/* Needs Review */}
                <div className="text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-xl font-bold text-foreground font-mono">{data.needsReview}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Needs Review</div>
                </div>

                {/* Waiting on Carrier */}
                <div className="text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Clock className="w-3.5 h-3.5 text-blue-500" />
                        <span className="text-xl font-bold text-foreground font-mono">{data.waitingOnCarrier}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Waiting on Carrier</div>
                </div>
            </div>

            {/* Last Action */}
            {data.lastAction && (
                <div className="px-5 pb-4 pt-0">
                    <div className="text-xs text-muted-foreground border-t border-border pt-3 flex items-center gap-2">
                        <span className="shrink-0 font-medium">Last action:</span>
                        <span className="truncate">{data.lastAction.summary}</span>
                        {lastActionTime && (
                            <span className="shrink-0 text-muted-foreground/60">· {lastActionTime}</span>
                        )}
                    </div>
                </div>
            )}
        </motion.div>
    );
}
